import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bookmark, Check, ChevronLeft, ChevronRight, Eraser, Gift, MessageCircleQuestion, NotebookPen, PenLine, Play, Share2, Sunrise, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/cast/Common";
import { PlusGate } from "@/components/cast/PlusGate";
import { MARKS, loadPrefs, savePrefs, themeOf } from "@/lib/deep";
import { ReaderBar, useChapterAudio } from "@/components/cast/deep/ReaderBar";
import { Guide, GUIDE_ICON } from "@/components/cast/deep/Guide";
import { HighlightItem } from "@/components/cast/deep/HighlightItem";
import { NoteDialog } from "@/components/cast/deep/NoteDialog";
import { VerseCardDialog } from "@/components/cast/VerseCard";
import { SendVerseDialog } from "@/components/cast/deep/SendVerseDialog";
import { PlusWelcome } from "@/components/cast/deep/PlusWelcome";
import { DeepStreak } from "@/components/cast/deep/DeepStreak";
import confetti from "canvas-confetti";

function useKids() {
  const [kids, setKids] = useState<any[]>([]);
  useEffect(() => { api.get("/children").then((r) => setKids(r.data)).catch(() => {}); }, []);
  return kids;
}

export default function DeepDive() {
  const { book, ch } = useParams();
  return book && ch ? <Reader book={+book} ch={+ch} /> : <DeepIndex />;
}

function Highlights({ abbr }: { abbr: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [all, setAll] = useState(false);
  useEffect(() => { api.get("/deep/marks").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const setNote = (i: number, note: string) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, note } : r)));
  const kids = useKids();
  if (!rows.length) return null;
  return (
    <section className="mt-12" data-testid="deep-highlights">
      <p className="eyebrow">My highlights</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {rows.slice(0, all ? rows.length : 12).map((m, i) => <HighlightItem key={m.ref} m={m} abbr={abbr} kids={kids} onNote={(n) => setNote(i, n)} />)}
      </div>
      {rows.length > 12 && <button data-testid="deep-highlights-toggle" onClick={() => setAll(!all)} className="mt-4 text-sm font-semibold text-[#C86D51] hover:underline">{all ? "Show fewer" : `Show all ${rows.length}`}</button>}
    </section>
  );
}

function DeepIndex() {
  const [data, setData] = useState<any>(null);
  const [testament, setTestament] = useState("old");
  const [open, setOpen] = useState<number | null>(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/deep/books").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader />;
  const books = data.books.filter((b: any) => b.testament === testament);
  const pick = (b: number, c: number) => (data.locked ? toast("Deep Dive is part of Cast Plus") : nav(`/deep-dive/${b}/${c}`));
  return (
    <div data-testid="deep-dive-page">
      <PageHeader metaphor="Deep Dive" label="Cast Plus · Bible" title="Sit with the whole story." subtitle="Read any chapter in your Bible version, highlight in colour, listen along, and ask the Study Guide when a passage is hard." />
      {data.locked && <div className="mb-10"><PlusGate testId="deep-gate" title="Deep Dive is for Cast Plus members" body="Get the immersive Bible with colour highlights, page colours, day and night reading, narrated chapters and the Study Guide." /></div>}
      {!data.locked && <PlusWelcome />}
      {!data.locked && <DeepStreak />}
      {!data.locked && data.place && (
        <Link to={`/deep-dive/${data.place.book}/${data.place.ch}`} data-testid="deep-continue" className="mb-10 flex items-center gap-4 rounded-3xl bg-[#1E293B] p-6 text-amber-50 transition-transform hover:-translate-y-0.5">
          <Bookmark className="h-7 w-7 fill-amber-300 text-amber-300" />
          <span className="flex-1"><span className="block text-xs font-semibold uppercase tracking-[0.2em] text-amber-200/80">Pick up where you left off</span><span className="font-display text-3xl font-semibold">{data.place.name} {data.place.ch}</span></span>
          <ChevronRight className="h-6 w-6" />
        </Link>
      )}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
        {[["old", "Old Testament"], ["new", "New Testament"]].map(([k, l]) => (
          <button key={k} data-testid={`deep-testament-${k}`} onClick={() => { setTestament(k); setOpen(null); }} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${testament === k ? "border-[#1E293B] bg-[#1E293B] text-white" : "border-[#E2DBCE] bg-white text-slate-700"}`}>{l}</button>
        ))}
        </div>
        {!data.locked && <Link to="/deep-dive/notes" data-testid="deep-notes-link" className="flex items-center gap-1.5 rounded-full border border-[#E2DBCE] bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-[#FBF7EF]"><NotebookPen className="h-4 w-4 text-[#C86D51]" />My Notes</Link>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {books.map((b: any) => (
          <button key={b.n} data-testid={`deep-book-${b.n}`} onClick={() => setOpen(open === b.n ? null : b.n)} className={`rounded-xl border px-3 py-3 text-left transition-colors ${open === b.n ? "border-[#C86D51] bg-[#FFF5EE]" : "border-[#E2DBCE] bg-white hover:bg-[#FBF7EF]"}`}>
            <span className="block font-display text-lg font-semibold text-slate-900">{b.name}</span><span className="text-xs text-slate-500">{b.chapters} {b.chapters === 1 ? "chapter" : "chapters"}</span>
          </button>
        ))}
      </div>
      <AnimatePresence>
        {open && (
          <motion.div key={open} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="card-warm mt-6 p-5" data-testid="deep-chapters">
            <p className="mb-3 font-display text-2xl font-semibold text-slate-900">{data.books[open - 1].name}</p>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: data.books[open - 1].chapters }, (_, i) => (
                <button key={i} data-testid={`deep-chapter-${i + 1}`} onClick={() => pick(open, i + 1)} className="h-10 w-10 rounded-lg border border-[#E2DBCE] text-sm font-semibold text-slate-700 transition-colors hover:border-[#C86D51] hover:bg-[#FFF5EE]">{i + 1}</button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!data.locked && <Highlights abbr={data.abbr || "KJV"} />}
    </div>
  );
}

function Reader({ book, ch }: { book: number; ch: number }) {
  const [data, setData] = useState<any>(null);
  const [locked, setLocked] = useState(false);
  const [prefs, setP] = useState(loadPrefs);
  const [sel, setSel] = useState<number[]>([]);
  const [guide, setGuide] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [card, setCard] = useState(false);
  const [gift, setGift] = useState(false);
  const kids = useKids();
  const audio = useChapterAudio(book, ch);
  const t = themeOf(prefs.theme);
  const setPrefs = (p: any) => { setP(p); savePrefs(p); };
  useEffect(() => {
    setData(null); setSel([]);
    api.get(`/deep/read/${book}/${ch}`).then((r) => setData(r.data)).catch((e) => (e?.response?.status === 403 ? setLocked(true) : toast.error(errMsg(e))));
    window.scrollTo({ top: 0 });
  }, [book, ch]);
  const selRef = useMemo(() => {
    const s = [...sel].sort((a, b) => a - b);
    return s.length ? `${data?.name} ${ch}:${s.length > 1 && s[s.length - 1] - s[0] === s.length - 1 ? `${s[0]}-${s[s.length - 1]}` : s.join(",")}` : "";
  }, [sel, data, ch]);
  const cardVerse = useMemo(() => {
    const s = [...sel].sort((a, b) => a - b);
    return { ref: selRef, text: (data?.verses || []).filter((v: any) => s.includes(v.n)).map((v: any) => v.text).join(" "), version: data?.abbr || "" };
  }, [sel, selRef, data]);
  const cardNote = sel.length === 1 ? data?.notes?.[sel[0]] : "";
  const reading = audio.current;
  useEffect(() => {
    if (reading) document.querySelector(`[data-testid="verse-${reading}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [reading]);
  if (locked) return <PlusGate testId="reader-gate" title="Deep Dive is for Cast Plus members" body="Read, highlight and listen to every chapter with Cast Plus." />;
  if (!data) return <Loader />;
  const mark = async (color: string | null) => {
    if (!color && sel.some((n) => data.notes?.[n]) && !window.confirm("Removing this highlight also deletes your note. Continue?")) return;
    try {
      await api.put("/deep/marks", { book, ch, verses: sel, color });
      const picked = sel;
      setData((d: any) => {
        const marks = { ...d.marks }, notes = { ...d.notes };
        picked.forEach((n) => { if (color) marks[String(n)] = color; else { delete marks[String(n)]; delete notes[String(n)]; } });
        return { ...d, marks, notes };
      });
      setSel([]);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (n: number) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  const markRead = async () => {
    try {
      const { data: s } = await api.post(`/deep/read/${book}/${ch}/done`);
      setData((d: any) => ({ ...d, read_today: true }));
      if (s.first_today) {
        confetti({ particleCount: 70, spread: 70, origin: { y: 0.75 }, colors: ["#F5C98B", "#C86D51", "#FFFFFF"], disableForReducedMotion: true });
        toast.success(s.current > 1 ? `${s.current} days in a row. Keep going!` : "Day 1 of your reading streak!");
      } else toast.success("Another chapter read today");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const chapterLink = (p: number[] | null, dir: string) => p && (
    <Link to={`/deep-dive/${p[0]}/${p[1]}`} data-testid={`reader-${dir}`} className="flex items-center gap-1 rounded-full border px-4 py-2 text-sm font-semibold" style={{ borderColor: t.line, color: t.text }}>
      {dir === "prev" && <ChevronLeft className="h-4 w-4" />}{dir === "prev" ? "Previous" : "Next chapter"}{dir === "next" && <ChevronRight className="h-4 w-4" />}
    </Link>
  );
  return (
    <div data-testid="reader-page" className="-mx-4 sm:mx-0">
      <div className="relative overflow-hidden rounded-none px-5 py-8 transition-colors duration-500 sm:rounded-3xl sm:px-10 sm:py-12" style={{ background: t.bg, color: t.text }} data-testid={`reader-theme-${t.id}`}>
        {t.night && <div className="pointer-events-none absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(1px 1px at 12% 18%, #fff8, transparent), radial-gradient(1px 1px at 78% 12%, #fff6, transparent), radial-gradient(1.5px 1.5px at 60% 30%, #fde68a88, transparent), radial-gradient(1px 1px at 30% 8%, #fff5, transparent)" }} />}
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <Link to="/deep-dive" data-testid="reader-back" className="flex items-center gap-1 text-sm font-semibold" style={{ color: t.sub }}><ChevronLeft className="h-4 w-4" />All books</Link>
          <ReaderBar prefs={prefs} setPrefs={setPrefs} audio={audio} fg={t.text} line={t.line} />
        </div>
        <div className="relative mx-auto mt-10 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: t.sub }}>{data.abbr} · Chapter {ch} of {data.chapters}</p>
          <h1 className="mt-2 font-display text-5xl font-semibold sm:text-6xl" data-testid="reader-title">{data.name} {ch}</h1>
          <p className="mt-8 font-scripture leading-[1.9]" style={{ fontSize: prefs.size }} data-testid="reader-text">
            {data.verses.map((v: any) => {
              const m = data.marks[v.n];
              const on = sel.includes(v.n);
              const reading = audio.current === v.n;
              const note = data.notes?.[v.n];
              return (
                <span key={v.n} data-testid={`verse-${v.n}`} onClick={() => toggle(v.n)} className="cursor-pointer rounded-md px-0.5 transition-colors duration-200 [box-decoration-break:clone]"
                  data-reading={reading ? "yes" : undefined}
                  style={{ background: m ? (t.night ? MARKS[m].night : MARKS[m].day) : undefined, textDecoration: on ? "underline dotted" : undefined, textUnderlineOffset: 6,
                    boxShadow: reading ? `0 0 0 3px ${t.night ? "rgba(253,230,138,0.45)" : "rgba(200,109,81,0.45)"}` : undefined, color: audio.current && !reading ? t.sub : undefined }}>
                  <sup className="mr-1 font-sans text-[0.55em] font-semibold" style={{ color: t.sub }}>{v.n}</sup>{v.text}
                  {note && <button data-testid={`verse-note-${v.n}`} aria-label="Open my note" onClick={(e) => { e.stopPropagation(); setNoteFor(v.n); }} className="ml-1 inline-flex -translate-y-0.5 rounded-full p-1 align-middle text-[#C86D51]"><PenLine className="h-3.5 w-3.5" /></button>}{" "}
                </span>
              );
            })}
          </p>
          <div className="mt-12 flex flex-col items-center gap-2 border-t pt-8" style={{ borderColor: t.line }}>
            {data.read_today
              ? <p className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold" style={{ background: t.night ? "rgba(253,230,138,0.15)" : "#FEF3C7", color: t.night ? "#FDE68A" : "#92400E" }} data-testid="reader-read-today"><Check className="h-4 w-4" />Read today</p>
              : <button data-testid="reader-mark-read" onClick={markRead} className="flex items-center gap-2 rounded-full bg-amber-300 px-6 py-3 text-sm font-semibold text-amber-950 shadow transition-transform hover:-translate-y-0.5"><Sunrise className="h-4 w-4" />I read this</button>}
            <p className="text-xs" style={{ color: t.sub }}>One chapter a day keeps your reading streak going</p>
          </div>
          <div className="mt-8 flex justify-between gap-3">{chapterLink(data.prev, "prev") || <span />}{chapterLink(data.next, "next")}</div>
        </div>
      </div>
      <AnimatePresence>
        {sel.length > 0 && (
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} data-testid="mark-bar"
            className="fixed inset-x-3 bottom-20 z-40 mx-auto flex max-w-lg flex-wrap items-center gap-1.5 rounded-2xl border border-[#E2DBCE] bg-white/95 p-2.5 shadow-xl backdrop-blur-md sm:gap-2 sm:p-3 lg:bottom-8">
            <span className="mr-1 w-full text-xs font-semibold text-slate-600 sm:w-auto" data-testid="mark-bar-ref">{selRef}</span>
            {Object.entries(MARKS).map(([k, c]) => <button key={k} aria-label={c.label} data-testid={`mark-${k}`} onClick={() => mark(k)} className="h-7 w-7 rounded-full sm:h-8 sm:w-8 border-2 border-white shadow ring-1 ring-slate-200 transition-transform hover:scale-110" style={{ background: c.dot }} />)}
            <button data-testid="mark-clear" aria-label="Remove highlight" onClick={() => mark(null)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><Eraser className="h-4 w-4" /></button>
            {sel.length === 1 && data.marks[sel[0]] && <button data-testid="mark-note" aria-label="Note" onClick={() => setNoteFor(sel[0])} className="rounded-full p-2 text-slate-600 hover:bg-slate-100"><PenLine className="h-4 w-4" /></button>}
            <button data-testid="mark-play" aria-label="Play from here" onClick={() => { audio.playFrom(Math.min(...sel)); setSel([]); }} className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-200"><Play className="h-3.5 w-3.5 fill-current" /><span className="hidden sm:inline">Play from here</span><span className="sm:hidden">Play</span></button>
            {kids.length > 0 && <button data-testid="mark-send" aria-label="Send to Kids Cove" onClick={() => setGift(true)} className="rounded-full p-2 text-[#E07A5F] hover:bg-rose-50"><Gift className="h-4 w-4" /></button>}
            <button data-testid="mark-share" aria-label="Share" onClick={() => setCard(true)} className="rounded-full p-2 text-slate-600 hover:bg-slate-100"><Share2 className="h-4 w-4" /></button>
            <button data-testid="mark-ask" onClick={() => { setPrompt(`Help me understand ${selRef}.`); setSel([]); setGuide(true); }} className="ml-auto flex items-center gap-1 rounded-full bg-[#1E293B] px-3 py-1.5 text-xs font-semibold text-white"><MessageCircleQuestion className="h-4 w-4" />Ask</button>
            <button data-testid="mark-cancel" aria-label="Cancel" onClick={() => setSel([])} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>
      {!sel.length && (
        <button data-testid="guide-open" onClick={() => { setPrompt(""); setGuide(true); }} className="fixed bottom-24 right-5 z-40 flex items-center gap-2 rounded-full bg-white py-1.5 pl-1.5 pr-4 shadow-xl ring-1 ring-[#E2DBCE] transition-transform hover:-translate-y-0.5 lg:bottom-8">
          <span className="relative"><img src={GUIDE_ICON} alt="" className="h-10 w-10 rounded-full object-cover" /><span className="absolute inset-0 animate-ping rounded-full ring-2 ring-amber-300 motion-reduce:hidden" style={{ animationDuration: "2.6s" }} /></span>
          <span className="text-sm font-semibold text-slate-800">Study Guide</span>
        </button>
      )}
      {noteFor && <NoteDialog open onOpenChange={(o) => !o && setNoteFor(null)} book={book} ch={ch} verse={noteFor} refText={`${data.name} ${ch}:${noteFor}`} note={data.notes?.[noteFor] || ""}
        onSaved={(n) => setData((d: any) => { const notes = { ...d.notes }; if (n) notes[String(noteFor)] = n; else delete notes[String(noteFor)]; return { ...d, notes }; })} />}
      <VerseCardDialog verse={cardVerse} noteText={cardNote} open={card} onOpenChange={setCard} />
      {kids.length > 0 && <SendVerseDialog open={gift} onOpenChange={setGift} kids={kids} book={book} ch={ch} verses={[...sel].sort((a, b) => a - b)} refText={selRef} text={cardVerse.text} onSent={() => setSel([])} />}
      <Guide open={guide} onOpenChange={setGuide} book={book} ch={ch} name={data.name} prompt={prompt} />
    </div>
  );
}
